// The pricing page's words, in the shape every language fills.
//
// The English object is the source each translation is made from, assembled
// from the same constants the English page prints ($lib/pricing/copy), so the
// source cannot say something the page does not. The numbers themselves are
// never translated: every language interpolates the same price and the same
// allowances from that module, so a translation cannot quote a different
// price or a different free allowance.

import {
  BILLED_FLAG_NOTE,
  BILLED_NEVER,
  BILLED_ONLY_BODY,
  BILLED_ONLY_TITLE,
  BILLED_RULE,
  BILLING_OUTCOMES,
  FREE_PLAN,
  FREE_START_REGISTERED,
  FREE_START_SANDBOX,
  FREE_START_TOPUP,
  PRICE_FLAT_NOTE,
  PRICE_HEADLINE,
  PRICE_PER_DOCUMENT,
} from "$lib/pricing/copy";
import type { ScanStatus } from "$lib/report/types";
import { fromLocale } from "../load";
import type { TranslatedLocale } from "../locales";

/** The billing promise, as the callout block prints it. */
export interface BilledOnlyCopy {
  readonly title: string;
  readonly body: string;
}

export interface PricingCopy {
  readonly title: string;
  /** At most 160 characters. */
  readonly description: string;
  readonly heading: string;
  /** Printed after the price: "per document". */
  readonly perDocument: string;
  readonly flatNote: string;
  /**
   * The free monthly allowance, printed under the price. "Free" and the
   * monthly number stay in the same line, in every language.
   */
  readonly freePlan: string;
  readonly billedOnly: BilledOnlyCopy;
  readonly getKey: string;
  readonly tryDemo: string;
  readonly howBillingWorks: string;
  readonly billedRule: string;
  readonly billedNever: string;
  readonly billedFlagNote: string;
  readonly table: {
    readonly result: string;
    readonly meaning: string;
    readonly cost: string;
    readonly free: string;
  };
  /** Every status the API answers with, in this language. */
  readonly outcomes: Readonly<
    Record<ScanStatus, { readonly label: string; readonly meaning: string }>
  >;
  readonly startingOut: string;
  readonly noAccount: string;
  readonly afterRegister: string;
  readonly afterThat: string;
  readonly freeStartSandbox: string;
  readonly freeStartRegistered: string;
  readonly freeStartTopup: string;
  /**
   * The paragraph under the three cards, as HTML: it carries one link, to the
   * documentation (`<a href="/docs" rel="external">`).
   */
  readonly includedHtml: string;
  /**
   * The line after it, as HTML: it carries two links, to the comparison
   * (`<a href="/compare">`) and to the free API page (`<a href="/free-passport-ocr-api">`).
   */
  readonly compareHtml: string;
  /** The product's description in the structured data. */
  readonly productDescription: string;
}

function outcomesOf(): PricingCopy["outcomes"] {
  const entries = Object.entries(BILLING_OUTCOMES).map(([status, outcome]) => [
    status,
    { label: outcome.label, meaning: outcome.meaning },
  ]);
  return Object.fromEntries(entries) as PricingCopy["outcomes"];
}

export const PRICING_EN: PricingCopy = {
  title: `Pricing – ${PRICE_PER_DOCUMENT} per recognised document`,
  description:
    `${FREE_PLAN}, then ${PRICE_HEADLINE}, the same at any volume. Billed only for documents ` +
    "we actually recognise – a failed scan costs nothing.",
  heading: "Pricing",
  perDocument: "per document",
  flatNote: PRICE_FLAT_NOTE,
  freePlan: `${FREE_PLAN} on your own account, then ${PRICE_PER_DOCUMENT} per recognised document.`,
  billedOnly: { title: BILLED_ONLY_TITLE, body: BILLED_ONLY_BODY },
  getKey: "Get your key",
  tryDemo: "Try the live demo",
  howBillingWorks: "How billing works",
  billedRule: BILLED_RULE,
  billedNever: BILLED_NEVER,
  billedFlagNote: BILLED_FLAG_NOTE,
  table: { result: "Result", meaning: "What it means", cost: "Cost", free: "Free" },
  outcomes: outcomesOf(),
  startingOut: "Starting out",
  noAccount: "No account",
  afterRegister: "After you register",
  afterThat: "Then",
  freeStartSandbox: FREE_START_SANDBOX,
  freeStartRegistered: FREE_START_REGISTERED,
  freeStartTopup: FREE_START_TOPUP,
  includedHtml:
    "Everything else is included at the same price: history retention you choose, the portrait " +
    "crop, MRZ check digits, the cross-checks between the machine-readable zone and the visual " +
    'zone, and the full field set. See the <a href="/docs" rel="external">documentation</a> for ' +
    "the request options and the response shape.",
  compareHtml:
    'To weigh the price, the <a href="/compare">price comparison with Textract and Mindee</a> ' +
    "sets it beside the prices other services publish, and you can " +
    '<a href="/free-passport-ocr-api">try it free with no account</a> first.',
  productDescription:
    "One passport, identity card or driver licence recognised from an image and returned as " +
    "structured JSON. Charged only when a document was actually read.",
};

const modules = import.meta.glob("../locales/*/pricing.ts");

export function loadPricingCopy(locale: TranslatedLocale): Promise<PricingCopy> {
  return fromLocale<PricingCopy>(modules, locale, "pricing");
}
